import type { DragEndEvent } from '@dnd-kit/core';
import type { TFunction } from 'i18next';
import type { Dispatch, SetStateAction } from 'react';
import type { IContent, IFeatureFamily, ISection } from '../../../../types';
import type { ICategoryDndSource, ICategoryDragData } from '../../../../types/ICategoryTypes';
import {
  ECategoryErrorType,
  ECategoryType,
  EDropCategory,
  EFeatureFamilyType,
} from '../../../data/Constants';
import {
  findAndRemoveNode,
  findSectionById,
  findSectionInTree,
  isInvalidCategoryDrop,
  isTargetMultiNumeric,
  isValidItemDrop,
  showCategoryError,
} from '../../../services/CategoryHelper';
import type { AppDispatch } from '../../../store/AppStore';
import { updateCategoryModify } from '../../../store/states/CategorySlice';

interface IDragOperationParams {
  dispatch: AppDispatch;
  globalUnassignedItems: IFeatureFamily[];
  isValidNumericItem: (over: ICategoryDndSource) => boolean;
  sections: ISection[];
  selectedSection: ISection | null;
  setGlobalUnassignedItems: Dispatch<SetStateAction<IFeatureFamily[]>>;
  setIsValidDragItem: Dispatch<SetStateAction<boolean>>;
  setSections: Dispatch<SetStateAction<ISection[]>>;
  t: TFunction;
}

interface IMoveItemParams {
  sections: ISection[];
  targetSection: ISection;
  currentSection: ISection | null;
  fromZone: string;
  itemId: string;
  isMultiNumeric: boolean;
  globalUnassignedItems: IFeatureFamily[];
  setGlobalUnassignedItems: Dispatch<SetStateAction<IFeatureFamily[]>>;
}

interface IMoveItemBetweenZonesParams {
  sections: ISection[];
  currentSection: ISection | null;
  fromZone: string;
  toZone: string;
  itemId: string;
  over: ICategoryDndSource;
  globalUnassignedItems: IFeatureFamily[];
  setGlobalUnassignedItems: Dispatch<SetStateAction<IFeatureFamily[]>>;
}

export const getCategoryDragData = (
  source: ICategoryDndSource | null
): ICategoryDragData | undefined => source?.data.current;

export const determineTargetZone = (over: ICategoryDndSource): string => {
  const overData = getCategoryDragData(over);
  if (overData?.container === EDropCategory.Assigned) {
    return EDropCategory.Assigned;
  }
  if (overData?.type === EDropCategory.Category) {
    return String(over.id);
  }
  if (over.id === EDropCategory.Assigned || over.id === EDropCategory.Unassigned) {
    return String(over.id);
  }
  return overData?.container ?? String(over.id);
};

export const isValidNumericItem = (
  activeData: ICategoryDragData | undefined,
  over: ICategoryDndSource,
  selectedSection: ISection | null
): boolean => {
  const isTargetCategory = String(over.id).startsWith(`${EDropCategory.Category}-`);
  return isValidItemDrop(selectedSection?.type, activeData?.itemType, isTargetCategory);
};

export const isForbiddenCategoryDrop = (
  sections: ISection[],
  activeData: ICategoryDragData | undefined,
  overData: ICategoryDragData | undefined,
  dispatch: AppDispatch,
  t: TFunction
): boolean => {
  if (activeData?.type !== EDropCategory.Category || overData?.type !== EDropCategory.Category) {
    return false;
  }

  const draggedNode = activeData.sectionId ? findSectionById(sections, activeData.sectionId) : null;
  const targetNode = overData.sectionId ? findSectionById(sections, overData.sectionId) : null;
  return !!(
    draggedNode &&
    targetNode &&
    isInvalidCategoryDrop(sections, draggedNode, targetNode, false, dispatch, t)
  );
};

const moveCategoryIntoCategory = (
  sections: ISection[],
  draggedId: string,
  targetId: string,
  dispatch: AppDispatch,
  t: TFunction
): ISection[] => {
  const draggedNode = findSectionById(sections, draggedId);
  const targetNode = findSectionById(sections, targetId);

  if (!draggedNode || !targetNode) {
    return sections;
  }

  if (isInvalidCategoryDrop(sections, draggedNode, targetNode, true, dispatch, t)) {
    return sections;
  }

  const { newSections, targetNode: nextTargetNode } = findAndRemoveNode(
    sections,
    draggedId,
    targetId
  );

  if (!nextTargetNode) {
    return sections;
  }

  nextTargetNode.sections ??= [];
  draggedNode.hideGeneral = true;
  nextTargetNode.sections.push(draggedNode);
  return [...newSections];
};

const handleCategoryToCategoryDrop = (
  active: DragEndEvent['active'],
  over: NonNullable<DragEndEvent['over']>,
  itemId: string,
  {
    dispatch,
    sections,
    setSections,
    t,
  }: Pick<IDragOperationParams, 'dispatch' | 'sections' | 'setSections' | 't'>
): boolean => {
  const activeData = getCategoryDragData(active);
  const overData = getCategoryDragData(over);
  if (activeData?.type !== EDropCategory.Category || overData?.type !== EDropCategory.Category) {
    return false;
  }

  const targetSectionId = overData.sectionId;
  if (!targetSectionId) {
    return true;
  }

  const draggedSectionId = itemId.replace(`${EDropCategory.Category}-`, '');
  if (draggedSectionId === targetSectionId) {
    return true;
  }

  const updatedSections = moveCategoryIntoCategory(
    sections,
    draggedSectionId,
    targetSectionId,
    dispatch,
    t
  );

  if (updatedSections !== sections) {
    setSections(updatedSections);
    dispatch(updateCategoryModify());
  }

  return true;
};

const moveItemToCategoryGeneric = ({
  sections,
  targetSection,
  currentSection,
  fromZone,
  itemId,
  isMultiNumeric,
  globalUnassignedItems,
  setGlobalUnassignedItems,
}: IMoveItemParams): ISection[] => {
  const newTargetSection = findSectionInTree(sections, targetSection.code);
  const newCurrentSection = currentSection
    ? findSectionInTree(sections, currentSection.code)
    : null;

  if (fromZone === EDropCategory.Unassigned) {
    const itemToMove = globalUnassignedItems.find((item) => item.code === itemId);
    if (
      itemToMove &&
      (!isMultiNumeric || itemToMove.familyType === EFeatureFamilyType.Numeric) &&
      newTargetSection
    ) {
      const content: IContent = {
        code: itemToMove.code,
        type: itemToMove.familyType,
        description: itemToMove.description,
      };
      newTargetSection.contents = [...(newTargetSection.contents || []), content];
      setGlobalUnassignedItems((prev) => prev.filter((item) => item.code !== itemId));
    }
  } else if (fromZone === EDropCategory.Assigned && newCurrentSection && newTargetSection) {
    const itemToMove = newCurrentSection.contents?.find((item) => item.code === itemId);
    if (itemToMove && (!isMultiNumeric || itemToMove.type === EFeatureFamilyType.Numeric)) {
      newCurrentSection.contents =
        newCurrentSection.contents?.filter((item) => item.code !== itemId) ?? [];
      newTargetSection.contents = [...(newTargetSection.contents || []), { ...itemToMove }];
    }
  }

  return sections;
};

const moveItemToCategory = (
  sections: ISection[],
  currentSection: ISection | null,
  toZone: string,
  fromZone: string,
  itemId: string,
  globalUnassignedItems: IFeatureFamily[],
  setGlobalUnassignedItems: Dispatch<SetStateAction<IFeatureFamily[]>>
): ISection[] => {
  const targetSectionId = toZone.replace(`${EDropCategory.Category}-`, '');
  const targetSection = findSectionById(sections, targetSectionId);

  if (!targetSection) {
    return sections;
  }

  return moveItemToCategoryGeneric({
    sections,
    targetSection,
    currentSection,
    fromZone,
    itemId,
    isMultiNumeric: targetSection.type === ECategoryType.MultiNumeric,
    globalUnassignedItems,
    setGlobalUnassignedItems,
  });
};

const moveItemBetweenZones = ({
  sections,
  currentSection,
  fromZone,
  toZone,
  itemId,
  over,
  globalUnassignedItems,
  setGlobalUnassignedItems,
}: IMoveItemBetweenZonesParams): ISection[] => {
  if (!currentSection) {
    return sections;
  }
  const newCurrentSection = findSectionInTree(sections, currentSection.code);

  if (!newCurrentSection) {
    return sections;
  }

  if (fromZone === EDropCategory.Unassigned && toZone === EDropCategory.Assigned) {
    const itemToMove = globalUnassignedItems.find((item) => item.code === itemId);
    if (itemToMove) {
      if (
        newCurrentSection.type === ECategoryType.MultiNumeric &&
        itemToMove.familyType !== EFeatureFamilyType.Numeric
      ) {
        return sections;
      }

      const content: IContent = {
        code: itemToMove.code,
        type: itemToMove.familyType,
        description: itemToMove.description,
      };
      newCurrentSection.contents = [...(newCurrentSection.contents || []), content];
      setGlobalUnassignedItems((prev) => prev.filter((item) => item.code !== itemId));
    }
  } else if (fromZone === EDropCategory.Assigned && toZone === EDropCategory.Assigned) {
    const oldIndex = newCurrentSection.contents?.findIndex((item) => item.code === itemId) ?? -1;
    const newIndex =
      newCurrentSection.contents?.findIndex((item) => item.code === String(over.id)) ?? -1;
    if (oldIndex !== -1 && newIndex !== -1) {
      const contentsCopy = [...(newCurrentSection.contents ?? [])];
      const [movedItem] = contentsCopy.splice(oldIndex, 1);
      contentsCopy.splice(newIndex, 0, movedItem);
      newCurrentSection.contents = contentsCopy;
    }
  }

  return sections;
};

export const handleCategoryDragEnd = (event: DragEndEvent, params: IDragOperationParams): void => {
  const { active, over } = event;
  if (!over) {
    return;
  }

  const itemId = String(active.id);
  const fromZone = getCategoryDragData(active)?.container ?? '';
  const toZone = determineTargetZone(over);

  if (fromZone === EDropCategory.Assigned && toZone === EDropCategory.Unassigned) {
    showCategoryError(params.dispatch, params.t, ECategoryErrorType.INVALID_DRAG_DROP);
    return;
  }

  if (handleCategoryToCategoryDrop(active, over, itemId, params)) {
    return;
  }

  if (isTargetMultiNumeric(over) && !params.isValidNumericItem(over)) {
    showCategoryError(params.dispatch, params.t, ECategoryErrorType.MULTI_NUMERIC_WARNING);
    params.setIsValidDragItem(true);
    return;
  }

  const updatedSections = toZone.startsWith(`${EDropCategory.Category}-`)
    ? moveItemToCategory(
        params.sections,
        params.selectedSection,
        toZone,
        fromZone,
        itemId,
        params.globalUnassignedItems,
        params.setGlobalUnassignedItems
      )
    : moveItemBetweenZones({
        sections: params.sections,
        currentSection: params.selectedSection,
        fromZone,
        toZone,
        itemId,
        over,
        globalUnassignedItems: params.globalUnassignedItems,
        setGlobalUnassignedItems: params.setGlobalUnassignedItems,
      });

  params.setSections(updatedSections);
  params.dispatch(updateCategoryModify());
};
